import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaBook, FaBookOpen, FaFlask, FaFistRaised, FaSwimmingPool, FaGamepad } from 'react-icons/fa';
import { useLanguage } from '../context/LanguageContext';

interface Interest {
  name: string;
  icon: React.ReactNode;
  description: string;
}

const interests: Interest[] = [
  {
    name: 'interests.reading.name',
    icon: <FaBook className="text-4xl" />,
    description: 'interests.reading.description'
  },
  {
    name: 'interests.manga.name',
    icon: <FaBookOpen className="text-4xl" />,
    description: 'interests.manga.description'
  },
  {
    name: 'interests.science.name',
    icon: <FaFlask className="text-4xl" />,
    description: 'interests.science.description'
  },
  {
    name: 'interests.kickboxing.name',
    icon: <FaFistRaised className="text-4xl" />,
    description: 'interests.kickboxing.description'
  },
  {
    name: 'interests.swimming.name',
    icon: <FaSwimmingPool className="text-4xl" />,
    description: 'interests.swimming.description'
  },
  {
    name: 'interests.gaming.name',
    icon: <FaGamepad className="text-4xl" />,
    description: 'interests.gaming.description'
  }
];

const InterestCard: React.FC<{ interest: Interest; index: number }> = ({ interest, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const { t } = useLanguage();

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ scale: 1.05 }}
      className="card text-center"
    >
      <div className="text-secondary mb-4">{interest.icon}</div>
      <h3 className="text-xl font-bold text-secondary mb-2">{t(interest.name)}</h3>
      <p className="text-base-content/60">{t(interest.description)}</p>
    </motion.div>
  );
};

const Interests: React.FC = () => {
  const { t } = useLanguage();
  
  return (
    <section id="interests" className="section-padding">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="heading-secondary mb-4 text-primary">{t('interests.title')}</h2>
          <div className="w-24 h-1 bg-secondary mx-auto rounded-full" />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {interests.map((interest, index) => (
            <InterestCard key={index} interest={interest} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Interests; 